/**
 * IndexedDB Offline Queue for PWA
 *
 * Manages offline operations and syncs them when back online.
 * Stores pending API requests in IndexedDB and processes them via Background Sync.
 */

const DB_NAME = 'helm-offline';
const DB_VERSION = 1;
const STORE_NAME = 'offline-queue';

export interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  lastError?: string;
}

/**
 * Initialize IndexedDB database
 */
export async function initOfflineDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      // Handle VersionError: if existing DB has a higher version, delete and recreate
      if (request.error?.name === 'VersionError') {
        console.warn('[OfflineQueue] DB version mismatch, recreating database');
        const deleteReq = indexedDB.deleteDatabase(DB_NAME);
        deleteReq.onsuccess = () => {
          // Retry after deletion
          initOfflineDB().then(resolve).catch(reject);
        };
        deleteReq.onerror = () => reject(deleteReq.error);
      } else {
        reject(request.error);
      }
    };
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        console.log('[OfflineQueue] Object store created');
      }
    };
  });
}

/**
 * Add a request to the offline queue
 */
export async function addToQueue(
  url: string,
  method: string,
  headers: Record<string, string> = {},
  body?: any
): Promise<string> {
  const db = await initOfflineDB();
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const queuedRequest: QueuedRequest = {
    id,
    url,
    method,
    headers,
    body,
    timestamp: Date.now(),
    retryCount: 0,
    maxRetries: 3,
    status: 'pending',
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.add(queuedRequest);

    request.onsuccess = () => {
      console.log('[OfflineQueue] Request queued:', id);
      resolve(id);
    };
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Get all pending requests from the queue
 */
export async function getPendingRequests(): Promise<QueuedRequest[]> {
  const db = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      console.log('[OfflineQueue] Pending requests:', request.result.length);
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Update request status
 */
export async function updateRequestStatus(
  id: string,
  status: QueuedRequest['status'],
  lastError?: string
): Promise<void> {
  const db = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const request = getRequest.result as QueuedRequest;
      if (!request) {
        reject(new Error('Request not found'));
        return;
      }

      request.status = status;
      if (lastError) {
        request.lastError = lastError;
      }

      const updateRequest = store.put(request);
      updateRequest.onsuccess = () => {
        console.log('[OfflineQueue] Request updated:', id, status);
        resolve();
      };
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Increment retry count for a request
 */
export async function incrementRetryCount(id: string): Promise<void> {
  const db = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const request = getRequest.result as QueuedRequest;
      if (!request) {
        reject(new Error('Request not found'));
        return;
      }

      request.retryCount++;

      const updateRequest = store.put(request);
      updateRequest.onsuccess = () => {
        console.log('[OfflineQueue] Retry count incremented:', id, request.retryCount);
        resolve();
      };
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Remove a request from the queue
 */
export async function removeFromQueue(id: string): Promise<void> {
  const db = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log('[OfflineQueue] Request removed:', id);
      resolve();
    };
    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Clear all completed and failed requests (older than 24 hours)
 */
export async function cleanupOldRequests(): Promise<number> {
  const db = await initOfflineDB();
  const cutoffTime = Date.now() - 24 * 60 * 60 * 1000; // 24 hours ago
  let deletedCount = 0;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const queuedRequest = cursor.value as QueuedRequest;
        if (
          (queuedRequest.status === 'completed' || queuedRequest.status === 'failed') &&
          queuedRequest.timestamp < cutoffTime
        ) {
          cursor.delete();
          deletedCount++;
        }
        cursor.continue();
      }
    };

    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => {
      console.log('[OfflineQueue] Cleanup complete, deleted:', deletedCount);
      db.close();
      resolve(deletedCount);
    };
  });
}

/**
 * Get queue statistics
 */
export async function getQueueStats(): Promise<{
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}> {
  const db = await initOfflineDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const requests = request.result as QueuedRequest[];
      const stats = {
        total: requests.length,
        pending: requests.filter((r) => r.status === 'pending').length,
        processing: requests.filter((r) => r.status === 'processing').length,
        completed: requests.filter((r) => r.status === 'completed').length,
        failed: requests.filter((r) => r.status === 'failed').length,
      };
      resolve(stats);
    };

    request.onerror = () => reject(request.error);

    transaction.oncomplete = () => db.close();
  });
}

/**
 * Process a single queued request
 */
export async function processRequest(request: QueuedRequest): Promise<boolean> {
  try {
    await updateRequestStatus(request.id, 'processing');

    // Stored CSRF token may be older than the 30-min server TTL — refresh before replay.
    const { getFreshCSRFToken } = await import('../services/api/client');
    const freshToken = await getFreshCSRFToken();
    const headers = freshToken
      ? { ...request.headers, 'x-csrf-token': freshToken }
      : request.headers;

    const response = await fetch(request.url, {
      method: request.method,
      headers,
      body: request.body ? JSON.stringify(request.body) : undefined,
    });

    if (response.ok) {
      await updateRequestStatus(request.id, 'completed');
      console.log('[OfflineQueue] Request completed:', request.id);
      return true;
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('[OfflineQueue] Request failed:', request.id, error.message);
    // intentional: background retry, no user action needed — retry logic handles re-queuing

    if (request.retryCount >= request.maxRetries) {
      await updateRequestStatus(request.id, 'failed', error.message);
      console.log('[OfflineQueue] Request failed permanently:', request.id);
      return false;
    } else {
      await incrementRetryCount(request.id);
      await updateRequestStatus(request.id, 'pending', error.message);
      console.log('[OfflineQueue] Request queued for retry:', request.id);
      return false;
    }
  }
}

/**
 * Process all pending requests in the queue
 */
export async function processQueue(): Promise<{ succeeded: number; failed: number }> {
  const pendingRequests = await getPendingRequests();
  let succeeded = 0;
  let failed = 0;

  console.log('[OfflineQueue] Processing queue:', pendingRequests.length, 'requests');

  for (const request of pendingRequests) {
    const success = await processRequest(request);
    if (success) {
      succeeded++;
    } else {
      failed++;
    }
  }

  console.log('[OfflineQueue] Queue processed:', { succeeded, failed });
  return { succeeded, failed };
}

/**
 * Check if online and trigger sync if available
 */
export async function syncIfOnline(): Promise<void> {
  if (!navigator.onLine) {
    console.log('[OfflineQueue] Offline, skipping sync');
    return;
  }

  // Try to use Background Sync if available
  if ('serviceWorker' in navigator && 'sync' in (await navigator.serviceWorker.ready)) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (registration as any).sync.register('sync-operations');
      console.log('[OfflineQueue] Background sync registered');
    } catch (error) {
      console.error('[OfflineQueue] Background sync failed:', error);
      // intentional: background retry via fallback processQueue, no user action needed
      await processQueue();
    }
  } else {
    // No Background Sync support, process immediately
    console.log('[OfflineQueue] No Background Sync, processing immediately');
    await processQueue();
  }
}

// Initialize on module load
initOfflineDB().then(() => {
  console.log('[OfflineQueue] Database initialized');

  // Cleanup old requests on startup
  cleanupOldRequests();

  // Listen for online event
  window.addEventListener('online', () => {
    console.log('[OfflineQueue] Back online, syncing...');
    syncIfOnline();
  });
}).catch((error) => {
  console.error('[OfflineQueue] Database initialization failed:', error);
  // intentional: infrastructure init failure, queue silently disabled — requests will fail at network layer
});
